import { Component, forwardRef, inject, Input, OnInit } from '@angular/core';
import { ControlValueAccessor, FormBuilder, NG_VALUE_ACCESSOR, ReactiveFormsModule } from '@angular/forms';
import { MatButton } from '@angular/material/button';
import { MAT_DATE_FORMATS } from '@angular/material/core';
import { MatDatepickerModule } from '@angular/material/datepicker';
import { MatDividerModule } from '@angular/material/divider';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatInputModule } from '@angular/material/input';
import { TranslateModule } from '@ngx-translate/core';
import { DateTime } from 'luxon';
import { debounceTime, merge } from 'rxjs';
import { takeUntil, tap } from 'rxjs/operators';

import { DestroyService } from '../../../sdk/destroy';
import { dateTimeMask, dateTimeUnmask, InputMaskDirective, timeMask } from '../../../sdk/forms/mask';
import { TimePickerComponent } from '../time-picker';
import { DEFAULT_TIME_INTERVAL, generateIntervalTimeList, TIME_FORMAT } from '../utils';
import { DATE_TIME_FORMATS } from './datetime.config';

@Component({
  selector: 'rms-datetime-picker',
  imports: [
    ReactiveFormsModule,
    MatFormFieldModule,
    MatInputModule,
    MatDatepickerModule,
    MatDividerModule,
    TimePickerComponent,
    InputMaskDirective,
    TranslateModule,
    MatButton,
  ],
  templateUrl: './datetime-picker.component.html',
  providers: [
    {
      provide: NG_VALUE_ACCESSOR,
      useExisting: forwardRef(() => DatetimePickerComponent),
      multi: true,
    },
    { provide: MAT_DATE_FORMATS, useValue: DATE_TIME_FORMATS },
    DestroyService,
  ],
})
export class DatetimePickerComponent implements ControlValueAccessor, OnInit {
  @Input() public interval = DEFAULT_TIME_INTERVAL;
  @Input() public placeholder = 'DD/MM/YYYY HH:mm';
  @Input() public min: DateTime | null = null;
  @Input() public max: DateTime | null = null;

  private readonly fb = inject(FormBuilder);
  private readonly destroy$ = inject(DestroyService);

  public form = this.fb.group({
    dateTimeInput: this.fb.control<DateTime | null>(null),
    dateCalendar: this.fb.control<DateTime | null>(null),
    timeInput: this.fb.control<string | null>(null),
  });

  private timeList: string[] = [];
  private onChange?: (value: DateTime) => void;
  private onTouched?: () => void;

  public dateTimeMask = (value: string): string => dateTimeMask(value, this.timeList);
  public dateTimeUnmask = dateTimeUnmask;

  ngOnInit(): void {
    this.timeList = generateIntervalTimeList(this.interval);

    const emitValue$ = this.form.controls.dateTimeInput.valueChanges.pipe(
      debounceTime(100),
      tap((value) => {
        if (!value) {
          return;
        }
        this.onTouched?.();
        this.onChange?.(value);
      })
    );

    const syncCalendarWithDateTimeInput$ = this.form.controls.dateTimeInput.valueChanges.pipe(
      debounceTime(100),
      tap((inputValue) => {
        if (!inputValue || !inputValue.isValid) {
          return;
        }

        this.form.patchValue(
          {
            dateCalendar: inputValue,
            timeInput: inputValue.toFormat(TIME_FORMAT),
          },
          { emitEvent: false }
        );
      })
    );

    const syncDateTimeInputWithCalendar$ = this.form.controls.dateCalendar.valueChanges.pipe(
      debounceTime(100),
      tap((calendarValue) => {
        if (!calendarValue || !calendarValue.isValid) {
          return;
        }

        const time = this.form.controls.timeInput.value;

        if (!time) {
          this.form.patchValue({
            dateTimeInput: calendarValue,
          });

          return;
        }

        const { hour, minute } = DateTime.fromFormat(time ?? '', TIME_FORMAT);

        if (isNaN(hour) || isNaN(minute)) {
          return;
        }

        const calendarValueWithTime = calendarValue.set({ hour, minute });

        this.form.patchValue({
          dateTimeInput: calendarValueWithTime,
        });
      })
    );

    merge(emitValue$, syncCalendarWithDateTimeInput$, syncDateTimeInputWithCalendar$)
      .pipe(takeUntil(this.destroy$))
      .subscribe();
  }

  registerOnChange(fn: (value: DateTime) => void): void {
    this.onChange = fn;
  }

  registerOnTouched(fn: () => void): void {
    this.onTouched = fn;
  }

  setDisabledState(isDisabled: boolean): void {
    isDisabled ? this.form.disable() : this.form.enable();
  }

  /*
   * We support ISO and Luxon date formats for the initial value.
   * */
  writeValue(dateTime: DateTime | string | null): void {
    if (!dateTime) {
      return;
    }

    const date = typeof dateTime === 'string' ? DateTime.fromISO(dateTime) : dateTime;
    /*
     * The initial value may not satisfy our interval. So we do a mask to find the closest valid time
     * */
    const maskedTime = timeMask(date.toFormat(TIME_FORMAT), this.timeList);

    this.form.patchValue({
      dateCalendar: date,
      timeInput: maskedTime,
    });
  }
}
